import { GoalIcon, NetworkIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { SearchResult, SearchResultHighlights } from '@/shared/registry/types';

import { PRIORITIES, STATUSES, type GoalPriority, type GoalStatus } from '../api';

import { DeadlineChip, PriorityDot, StatusBadge } from './bits';

function Shell({ icon: Icon, children }: { icon: typeof GoalIcon; children: ReactNode }) {
  return (
    <>
      <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg border bg-background text-brand-text shadow-xs">
        <Icon className="size-[1.125rem]" strokeWidth={1.8} aria-hidden />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">{children}</span>
    </>
  );
}

/** Global search result of entity type `map` (meta: goalCount, ownerName, archived). */
export function MapSearchResult({
  result,
  highlights,
}: {
  result: SearchResult;
  highlights?: SearchResultHighlights;
}) {
  const { t } = useTranslation('goals');
  const meta = result.meta ?? {};
  const snippet = highlights?.snippet ?? result.snippet;
  return (
    <Shell icon={NetworkIcon}>
      <span className="truncate text-[0.9375rem] font-medium text-foreground">
        {highlights?.title ?? result.title}
      </span>
      <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>{t('search.map')}</span>
        {meta.ownerName && <span>{meta.ownerName}</span>}
        {meta.goalCount && <span>{t('card.goals', { count: Number(meta.goalCount) })}</span>}
        {meta.archived === 'true' && <span>{t('card.archived')}</span>}
      </span>
      {snippet && <span className="line-clamp-2 text-sm text-muted-foreground">{snippet}</span>}
    </Shell>
  );
}

/** Global search result of entity type `goal` (meta: mapTitle, status, priority, deadline). */
export function GoalSearchResult({
  result,
  highlights,
}: {
  result: SearchResult;
  highlights?: SearchResultHighlights;
}) {
  const meta = result.meta ?? {};
  const status = STATUSES.includes(meta.status as GoalStatus) ? (meta.status as GoalStatus) : undefined;
  const priority = PRIORITIES.includes(meta.priority as GoalPriority)
    ? (meta.priority as GoalPriority)
    : undefined;
  const snippet = highlights?.snippet ?? result.snippet;
  return (
    <Shell icon={GoalIcon}>
      <span className="flex min-w-0 items-center gap-2">
        {priority && <PriorityDot priority={priority} />}
        <span className="truncate text-[0.9375rem] font-medium text-foreground">
          {highlights?.title ?? result.title}
        </span>
      </span>
      <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {status && <StatusBadge status={status} />}
        {meta.mapTitle && <span>{meta.mapTitle}</span>}
        {meta.deadline && status && (
          <DeadlineChip goal={{ deadline: meta.deadline, deadlineHasTime: false, status }} compact />
        )}
      </span>
      {snippet && <span className="line-clamp-2 text-sm text-muted-foreground">{snippet}</span>}
    </Shell>
  );
}
